import styled, { css } from 'styled-components';
import Flex, { FlexProps } from '../flex/Flex';
import theme from '../../styles/theme';

export interface DatePickerStylesProps {
  months?: number;
  numberOfMonths?: number;
  bookingDate?: boolean;
  isStartDate?: boolean;
  isEndDate?: boolean;
  range?: string;
  variant?: string;
}

export interface ButtonProps {
  variant?: 'startOrEnd' | 'selected' | 'inrange' | 'disabled' | 'normal';
  isStartOrEndOfRowGrid?: 'startRowGrid' | 'endRowGrid';
}

const DatePickerGrid = styled.div<DatePickerStylesProps>`
  min-height: 200px;
  padding-top: ${theme.spacing['spacing-2']};
  ${({ months }) =>
    months &&
    months >= 2 &&
    css`
      min-width ${344 / months}px;
      min-height: 310px;
      &:nth-child(2n) {
        margin-left: ${theme.spacing['spacing-3']};
      }
    `}
`;

const DatePickerDay = styled.div<DatePickerStylesProps>`
  display: grid;
  grid-template-columns: repeat(
    ${({ numberOfMonths = 1 }) => numberOfMonths},
    322px
  );
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 46px);
  row-gap: ${theme.spacing['spacing-0']};
  justify-content: center;
`;

const DatePickerWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 46px);
  margin-bottom: ${theme.spacing['spacing-0']};
  justify-content: center;
`;

const DatePickerWeekContent = styled.div`
  text-align: center;
  height: 34px;
  width: 46px;
  padding: ${theme.spacing['spacing-0']};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  background: transparent;
  cursor: pointer;
  min-width: 24px;
  width: 24px;
  height: 24px;
  margin: 0 ${theme.spacing['spacing-2']};
  padding: ${theme.spacing['spacing-0']};
`;

const DatePickerBody = styled(Flex)<FlexProps>`
  position: relative;
  justify-content: center;
`;

const DatePickerFooter = styled.div<DatePickerStylesProps>`
  width: 100%;
  padding-top: ${theme.spacing['spacing-3']};
  padding-bottom: ${theme.spacing['spacing-3']};
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ bookingDate, numberOfMonths }) =>
    !!bookingDate &&
    numberOfMonths !== 1 &&
    css`
      padding-left: ${theme.spacing['spacing-11']};
      padding-right: ${theme.spacing['spacing-11']};
    `}
`;

const DatePickerButtons = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  width: 100%;

  & a {
    padding-left: ${theme.spacing['spacing-4']};
  }
  & a div {
    text-decoration: none;
  }
  & a:hover {
    text-decoration: none;
  }
`;

const DatePickerButtonsRight = styled(Flex)`
  justify-content: flex-end;

  & button {
    height: 36px;
    padding: ${theme.spacing['spacing-2']} ${theme.spacing['spacing-4']};
    ${theme.textStyles['text-preset-10']};
    border-radius: ${theme.borderRadius['border-radius-3']};
    justify-content: center;
  }
  & button:last-child {
    margin-left: ${theme.spacing['spacing-3']};
  }
`;

const DatePickerWrapper = styled.div<{ $flat?: boolean }>`
  width: fit-content;
  display: flex;
  margin-top: ${theme.spacing['spacing-2']};
  background-color: ${theme.colors.neutral.bg.inverted};
  border: 1px solid #DFE6EB;
  box-shadow: ${({ $flat }) => ($flat ? 'none' : theme.elevations['elevation-2'])};
  border-radius: ${theme.borderRadius['border-radius-3']};
`;

const DatePickerCalendarBox = styled.div<DatePickerStylesProps>`
  justify-self: center;
  margin: ${theme.spacing['spacing-2']} 0;
  ${({ isStartDate, isEndDate, range }) => {
    if (isStartDate !== isEndDate && range) {
      if (isStartDate) {
        return css`
          background: linear-gradient(
            90deg,
            transparent 50%,
            ${theme.colors.primary.bg.weak} 20%
          );
        `;
      } else if (isEndDate) {
        return css`
          background: linear-gradient(
            90deg,
            ${theme.colors.primary.bg.weak} 50%,
            transparent 20%
          );
        `;
      }
    }
  }}
  &.inrange:hover {
    margin: ${theme.spacing['spacing-1']} 0;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary.bg.weak} 50%,
      transparent 50%
    );
    & button {
      ${() => dayVariants['startOrEnd']}
      & span {
        color: ${theme.colors.primary.text.strong};
      }
    }
  }
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 65px);
  justify-content: left;
`;

const Month = styled(DatePickerCalendarBox)``;

const Day = styled(DatePickerCalendarBox)`
  margin: ${theme.spacing['spacing-0']} 0;
`;

const DayContent = styled.button<ButtonProps>`
  border: 0;
  text-transform: uppercase;
  align-items: center;
  display: flex;
  width: 46px;
  height: 34px;
  padding: ${theme.spacing['spacing-0']};
  justify-content: center;
  ${(props) => dayVariants[props.variant] || dayVariants.normal}
  ${(props) => startOrEndOfRowGridVariant[props.isStartOrEndOfRowGrid]}
`;

const MonthContent = styled.button<ButtonProps>`
  border: 0;
  text-transform: uppercase;
  align-items: center;
  display: flex;
  width: 65px;
  height: 30px;
  padding: ${theme.spacing['spacing-3']};
  justify-content: center;

  ${(props) => dayVariants[props.variant] || dayVariants.normal}
  ${(props) => startOrEndOfRowGridVariant[props.isStartOrEndOfRowGrid]}
`;

const DatePickerHeader = styled.div`
  text-align: center;
  margin: ${theme.spacing['spacing-0']} ${theme.spacing['spacing-0']}
    ${theme.spacing['spacing-2']};
`;

const DatePickerAnchor = styled.div`
  /* Ancla posicionada (block, sin cambio de layout) que envuelve el input y el
     popper absoluto: el popper queda contenido in-tree y scrollea nativamente
     con el contenedor (Layout inner overflowY:auto, debajo del NavBar). */
  position: relative;
`;

const DatePickerPopper = styled.div<{ alignLeft?: boolean; top?: number; left?: number; width?: number }>`
  position: absolute;
  top: ${({ top }) => (top !== undefined ? `${top}px` : 'auto')};
  left: ${({ left }) => (left !== undefined ? `${left}px` : 'auto')};
  width: ${({ width }) => (width !== undefined ? `${width}px` : 'auto')};
  z-index: 999;
  ${({ alignLeft }) => alignLeft && css`
    margin-left: -200px; /* Desplazar hacia la izquierda relativo al padre */
  `}
`;

const startOrEndOfRowGridVariant = {
  startRowGrid: css`
    border-bottom-left-radius: ${theme.borderRadius['border-radius-4']};
    border-top-left-radius: ${theme.borderRadius['border-radius-4']};
  `,
  endRowGrid: css`
    border-bottom-right-radius: ${theme.borderRadius['border-radius-4']};
    border-top-right-radius: ${theme.borderRadius['border-radius-4']};
  `,
};

const dayVariants = {
  startOrEnd: css`
    background: ${theme.colors.primary.bg.regular};
    border-radius: ${theme.borderRadius['border-radius-4']};
    cursor: pointer;
  `,
  selected: css`
    background: ${theme.colors.primary.bg.weak};
    cursor: pointer;
  `,
  inrange: css`
    background: ${theme.colors.primary.bg.weak};
    cursor: pointer;
  `,
  disabled: css`
    background: transparent;
    cursor: auto !important;
    color: ${theme.colors.neutral.text.regular};
  `,
  normal: css`
    background: none;
    cursor: pointer;
  `,
};

const DatePickerOptions = styled.div`
  h6 {
    width: 250px;
    margin-bottom: ${theme.spacing['spacing-3']};
  }
  z-index:999;
  padding-top: ${theme.spacing['spacing-4']};
  padding-left: ${theme.spacing['spacing-3']};
  & ul {
    & li {
      display: block;
      margin-top: ${theme.spacing['spacing-4']};
      margin-right: 100%
    }
    list-style: none;
  }

  & label {
      margin-bottom: ${theme.spacing['spacing-4']};
      white-space: nowrap;
    }
  }
`;

const DatePickerCalendarControl = styled(Flex)`
  position: relative;
  align-items: center;
`;
const DatePickerCalendar = styled.div`
  position: relative;
  padding: ${theme.spacing['spacing-2']} ${theme.spacing['spacing-3']}
    ${theme.spacing['spacing-0']};
`;

export {
  DatePickerOptions,
  DatePickerPopper,
  DatePickerAnchor,
  DatePickerGrid,
  Month,
  MonthGrid,
  MonthContent,
  DatePickerDay,
  Day,
  DayGrid,
  DayContent,
  DatePickerWeek,
  DatePickerWeekContent,
  DatePickerCalendarBox,
  DatePickerHeader,
  NavButton,
  DatePickerBody,
  DatePickerButtons,
  DatePickerButtonsRight,
  DatePickerWrapper,
  DatePickerFooter,
  DatePickerCalendarControl,
  DatePickerCalendar,
};
